import {
  ALERT_MANAGER_EVENT_TYPE_LOCAL_TOAST,
  ALERT_MANAGER_EVENT_TYPE_TOAST,
} from '../../utils/spds/constants/alert-manager';
import { DEFAULT_TIMEOUT } from '../../components/spds/feedback/toast/container.gjs';
import { ERROR, SUCCESS, SYSTEM } from '../../components/spds/feedback/toast/message.gjs';
import Service, { service } from '@ember/service';

/**
 * Generates a user-friendly message for file upload notifications
 * @private
 * @param {number} countUploaded - The number of files uploaded
 * @param {File} file - The File object that was uploaded
 * @returns {string} A formatted message showing file count or filename
 */
function getFileUploadMessage(countUploaded, file) {
  return countUploaded > 1 ? `${countUploaded} files uploaded` : `"${file.name}" uploaded`;
}

/**
 * SPDS Toast Service
 *
 * Provides an API for displaying transient toast notifications.
 * Toasts auto-dismiss after a timeout and appear at the top of the viewport.
 * Supports both global toasts (visible across the app) and local toasts (scoped to containers).
 *
 * @class SpdsToastService
 * @extends Service
 */
export default class SpdsToastService extends Service {
  @service alertManager;

  /**
   * Displays a global toast notification
   * @private
   * @param {string} message - The message to display
   * @param {string} type - The toast type constant (ERROR, SUCCESS, or SYSTEM)
   * @param {number} timeout - Duration in milliseconds before auto-dismiss
   * @returns {Promise<void>}
   */
  #displayToast = async (message, type, timeout) => {
    await this.alertManager.broadcast(ALERT_MANAGER_EVENT_TYPE_TOAST, {
      message,
      type,
      timeout,
    });
  };

  /**
   * Displays a local toast notification scoped to a container
   * @private
   * @param {string} message - The message to display
   * @param {string} type - The toast type constant (ERROR, SUCCESS, or SYSTEM)
   * @param {number} timeout - Duration in milliseconds before auto-dismiss
   * @returns {Promise<void>}
   */
  #displayLocalToast = async (message, type, timeout) => {
    await this.alertManager.broadcast(ALERT_MANAGER_EVENT_TYPE_LOCAL_TOAST, {
      message,
      type,
      timeout,
    });
  };

  /**
   * Displays a global error toast
   * @param {string} message - The error message to display
   * @param {number} [timeout=3000] - Duration in milliseconds before auto-dismiss
   *
   * @example
   * this.toast.error('Failed to save changes');
   */
  error = (message, timeout = DEFAULT_TIMEOUT) => {
    this.#displayToast(message, ERROR, timeout);
  };

  /**
   * Displays a local success toast for file upload completion
   * Shows either the filename or count of multiple files uploaded.
   *
   * @param {number} countUploaded - The number of files uploaded
   * @param {File} file - The File object that was uploaded
   *
   * @example
   * this.toast.fileUploadLocalToast(1, fileObject);
   * // Shows: "filename.pdf" uploaded
   *
   * @example
   * this.toast.fileUploadLocalToast(3, fileObject);
   * // Shows: 3 files uploaded
   */
  fileUploadLocalToast(countUploaded, file) {
    this.localSuccess(getFileUploadMessage(countUploaded, file));
  }

  /**
   * Displays a global success toast for file upload completion
   * Shows either the filename or count of multiple files uploaded.
   *
   * @param {number} countUploaded - The number of files uploaded
   * @param {File} file - The File object that was uploaded
   *
   * @example
   * this.toast.fileUploadToast(1, fileObject);
   * // Shows: "filename.pdf" uploaded
   */
  fileUploadToast(countUploaded, file) {
    this.success(getFileUploadMessage(countUploaded, file));
  }

  /**
   * Displays a local error toast scoped to a container
   * @param {string} message - The error message to display
   * @param {number} [timeout=3000] - Duration in milliseconds before auto-dismiss
   *
   * @example
   * this.toast.localError('Invalid email format');
   */
  localError = (message, timeout = DEFAULT_TIMEOUT) => {
    this.#displayLocalToast(message, ERROR, timeout);
  };

  /**
   * Displays a local system toast scoped to a container
   * @param {string} message - The system message to display
   * @param {number} [timeout=3000] - Duration in milliseconds before auto-dismiss
   *
   * @example
   * this.toast.localSystem('Connection restored');
   */
  localSystem = (message, timeout = DEFAULT_TIMEOUT) => {
    this.#displayLocalToast(message, SYSTEM, timeout);
  };

  /**
   * Displays a local success toast scoped to a container
   * @param {string} message - The success message to display
   * @param {number} [timeout=3000] - Duration in milliseconds before auto-dismiss
   *
   * @example
   * this.toast.localSuccess('Settings saved');
   */
  localSuccess = (message, timeout = DEFAULT_TIMEOUT) => {
    this.#displayLocalToast(message, SUCCESS, timeout);
  };

  /**
   * Displays a global system toast
   * @param {string} message - The system message to display
   * @param {number} [timeout=3000] - Duration in milliseconds before auto-dismiss
   *
   * @example
   * this.toast.system('Scheduled maintenance in 10 minutes');
   */
  system = (message, timeout = DEFAULT_TIMEOUT) => {
    this.#displayToast(message, SYSTEM, timeout);
  };

  /**
   * Displays a global success toast
   * @param {string} message - The success message to display
   * @param {number} [timeout=3000] - Duration in milliseconds before auto-dismiss
   *
   * @example
   * this.toast.success('Changes saved successfully');
   */
  success = (message, timeout = DEFAULT_TIMEOUT) => {
    this.#displayToast(message, SUCCESS, timeout);
  };
}
